import React, { useState } from 'react';
import { PlusIcon, MinusIcon } from 'lucide-react';
type QA = {
  q: string;
  a: React.ReactNode;
};
const FAQS: QA[] = [{
  q: 'How does this work?',
  a: 'After purchase, you’ll receive a link to duplicate the template into your Notion.'
}, {
  q: 'How is GPS Journal different from other journalling tools?',
  a: <div className="space-y-3" data-id="element-25" data-mp="1ccdnin18hgt5">
        <p data-id="element-26" data-mp="2a1yx7cxl5wgn">
          Simply put: Traditional journals focus on the journalling, requiring anywhere from 10
          minutes to 1 hr each day. GPS Journal is a 5-minute journalling system intentionally
          designed to keep you aligned with your goals. These features include:
        </p>
        <ul className="list-disc space-y-2 pl-5" data-id="element-27" data-mp="1v5823fuy36wu">
          <li data-id="element-28" data-mp="1v4g5c1pm334z">
            <strong className="text-ink" data-id="element-29" data-mp="1v72ci08lbgmo">Goal visibility:</strong> keeping your goal front and
            center, which instills it in your mind.
          </li>
          <li data-id="element-30" data-mp="jorirbg9s64p">
            <strong className="text-ink" data-id="element-31" data-mp="jo9fhuv6wrna">Progress bars:</strong> the habits and projects that relate
            to your goals, visible in a simple, gamified way.
          </li>
          <li data-id="element-32" data-mp="1pn321ssms709">
            <strong className="text-ink" data-id="element-33" data-mp="1plic6gh9hc8q">Self-reflection:</strong> 5 daily questions that can be
            answered in 5 minutes and help prevent you from getting stuck.
          </li>
          <li data-id="element-34" data-mp="1fnm1fps8kjb5">
            <strong className="text-ink" data-id="element-35" data-mp="1fnccm5ixgi71">Today’s Improvement:</strong> your 1% action (picked by
            you) to help you do better today than you did yesterday.
          </li>
          <li data-id="element-36" data-mp="16o7ev1f6wsvx">
            <strong className="text-ink" data-id="element-37" data-mp="16oqto99jn15q">Habit and Project templates:</strong> habit stacks and
            time-blocking patterns from world-class performers that can help you stay aligned with
            your goals, and give you new pathways to progress.
          </li>
          <li data-id="element-38" data-mp="ixuxkzy21jso">
            <strong className="text-ink" data-id="element-39" data-mp="izfrcncjoyph">Self-awareness:</strong> over time, your own daily journals
            can give you insight into what works for you and what doesn’t, allowing you to
            organically discover your optimal way of moving forward.
          </li>
        </ul>
        <p data-id="element-40" data-mp="2kvn6cmvt35vj">GPS Journal contains all of these features in a simple and easy-to-use format.</p>
      </div>
}, {
  q: 'Why not just journal by hand?',
  a: 'Journalling by hand is great for self-discovery, but less than optimal for self-feedback. With this Notion template, it’s easy to see how frequently you engage your habits, how much time you spend in deep work, and with the click of a button discover the patterns in your journal entries. These features require a lot of extra time and energy if done by hand.'
}, {
  q: 'Can I make changes to GPS Journal?',
  a: 'Absolutely. GPS Journal is a Notion template, which means it becomes yours the moment you add it to your workspace. From then on, you can make any tweaks that you want. (Just please don’t share it with others, as it’s a paid template and part of my livelihood!) However, be aware that making changes to the template may make it harder for you to integrate some of the features native to the journal.'
}, {
  q: 'Is this really worth $27?',
  a: 'For $27, you gain instant access to a ready-to-use GPS Journal, saving you valuable time and effort by avoiding the need to create complex databases and layouts from scratch. This investment streamlines your planning, enhances alignment, and reduces disorder, allowing you to focus on what moves you forward.'
}, {
  q: 'Is my payment information secure?',
  a: 'Yes, 100%. With purchase via Notion’s marketplace, Notion entirely handles the payment. We never receive or see payment information.'
}, {
  q: 'Can I use this with Notion free plan?',
  a: 'Yes, you can use this Notion template with a free account. We specifically designed this version of GPS Journal to not require any Pro or advanced Notion features.'
}, {
  q: 'What is your refund policy?',
  a: 'You are eligible for a refund if you initiate a refund request within 14 days of purchase. This follows the Marketplace refund policy where this template is distributed.'
}];
function Item({
  item,
  open,
  onToggle
}: {
  item: QA;
  open: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-line" data-id="element-41" data-mp="2bsuj1mt15y4n">
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center justify-between gap-4 py-5 text-left" data-id="element-42" data-mp="26v2qcqkka7ze">
        <span className="text-base font-semibold text-ink sm:text-lg" data-id="element-43" data-mp="1j3dtqy0pixcn">{item.q}</span>
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-line text-ink" data-id="element-44" data-mp="1e88880b7vkp3">
          {open ? <MinusIcon className="h-4 w-4" data-id="element-45" data-mp="1sx4afgqle3hs" /> : <PlusIcon className="h-4 w-4" data-id="element-46" data-mp="1o4b78xarv37t" />}
        </span>
      </button>
      {open && <div className="pb-6 pr-10 text-[15px] leading-relaxed text-muted" data-id="element-47" data-mp="10egv6hya1iom">{item.a}</div>}
    </div>;
}
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section id="faqs" className="w-full bg-white py-24" data-id="element-48" data-mp="372zbnorxfjpc">
      <div className="mx-auto w-full max-w-3xl px-5 sm:px-8" data-id="element-49" data-mp="371fw60e3hcom">
        <h2 className="text-center text-3xl font-extrabold tracking-tight text-ink sm:text-4xl" data-id="element-50" data-mp="3bvk52p6x7usc">
          Frequently Asked Questions
        </h2>

        <div className="mt-12" data-id="element-51" data-mp="323wk974vdbzu">
          {FAQS.map((item, i) => <Item key={item.q} item={item} open={openIndex === i} onToggle={() => setOpenIndex(openIndex === i ? null : i)} data-id="element-52" data-mp="3223b9l6safoo" data-mpi={i} />)}
        </div>
      </div>
    </section>;
}